import api from '@/utils/api'
import type {
  AudienceRole,
  AttributionEventType,
  Recommendation,
  RecommendationAnalytics,
  RecommendationContracts,
  RecommendationPreferences,
  RecommendationRule,
  RecommendationRuleInput,
  SourceSurface,
  TargetSlot,
} from '@/types/recommendation'

export interface ResolveRecommendationInput {
  sourceSurface: SourceSurface
  questionBankId?: number
  chapter?: string
  audienceRole?: AudienceRole
}

export interface RecommendationAnalyticsFilters {
  from?: string
  to?: string
  questionBankId?: number
  intentCode?: string
  productCode?: string
  sourceSurface?: string
}

export const recommendationsApi = {
  async getContracts(): Promise<RecommendationContracts> {
    const response = await api.get('/recommendations/contracts')
    return response.data
  },

  async resolve(input: ResolveRecommendationInput): Promise<Recommendation | null> {
    const response = await api.post('/recommendations/resolve', input)
    return response.data
  },

  async resolvePublic(input: ResolveRecommendationInput): Promise<Recommendation | null> {
    const response = await api.post('/recommendations/public/resolve', input)
    return response.data
  },

  async getPreferences(): Promise<RecommendationPreferences> {
    const response = await api.get('/recommendations/preferences')
    return response.data
  },

  async updatePreferences(input: {
    audienceRole?: AudienceRole | null
    commercialRecommendationsEnabled?: boolean
  }): Promise<RecommendationPreferences> {
    const response = await api.patch('/recommendations/preferences', input)
    return response.data
  },

  async recordEvent(input: {
    eventId: string
    rid: string
    eventType: AttributionEventType
    targetSlot: TargetSlot
  }): Promise<{ accepted: boolean; duplicate: boolean }> {
    const response = await api.post('/recommendations/events', input)
    return response.data
  },

  async listRules(): Promise<RecommendationRule[]> {
    const response = await api.get('/recommendations/admin/rules')
    return response.data
  },

  async createRule(input: RecommendationRuleInput): Promise<RecommendationRule> {
    const response = await api.post('/recommendations/admin/rules', input)
    return response.data
  },

  async updateRule(id: number, input: RecommendationRuleInput): Promise<RecommendationRule> {
    const response = await api.patch(`/recommendations/admin/rules/${id}`, input)
    return response.data
  },

  async deleteRule(id: number): Promise<void> {
    await api.delete(`/recommendations/admin/rules/${id}`)
  },

  async getAnalytics(filters: RecommendationAnalyticsFilters = {}): Promise<RecommendationAnalytics> {
    const response = await api.get('/recommendations/admin/analytics', { params: filters })
    return response.data
  },
}
